import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SidebarComponent } from '../sidebar/sidebar.component';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { AddRetailerModalComponent, CategoryTypeEnum, KycStatusEnum, PaymentModeEnum } from './add-retailer-modal.component';
import { AssignDistributorModalComponent } from './assign-distributor-modal.component';
import { LinkedDistributorsModalComponent } from './linked-distributors-modal.component';
import { ToastService } from '../toast.service';
import { DebounceService } from '../debounce.service';

interface Retailer {
  id: string;
  retailer_name: string;
  email: string;
  customer_code?: string;
  status: string;
  purchase_limit: number;
  total_purchases: number;
  created_at: string;
  is_admin_approved: boolean;
  distributors?: any[];
}

interface Pagination {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

@Component({
  standalone: true,
  imports: [CommonModule, FormsModule, SidebarComponent, AddRetailerModalComponent, AssignDistributorModalComponent, LinkedDistributorsModalComponent],
  templateUrl: './retailers.component.html',
  styleUrls: ['./retailers.component.scss']
})
export class RetailersComponent implements OnInit {
  @ViewChild(AddRetailerModalComponent) addModal?: AddRetailerModalComponent;
  retailers: Retailer[] = [];
  pagination: Pagination = {
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 1,
    hasNext: false,
    hasPrev: false
  };
  searchTerm = '';
  loading = false;
  errorMsg = '';
  showAddModal = false;
  showAssignModal = false;
  assigningRetailerId = '';
  assigningRetailerName = '';
  userType = '';
  editingRetailerId: string | null = null;
  selectedRetailerData: any = null;

  // Delete confirmation
  showDeleteConfirm = false;

  // Linked Distributors modal
  showLinkedDistributorsModal = false;
  selectedRetailerForLinked: any = null;
  deletingRetailer: Retailer | null = null;
  deleting = false;

  constructor(
    private http: HttpClient,
    private toastService: ToastService,
    private debounceService: DebounceService
  ) { }

  ngOnInit() {
    this.loadUserType();
    this.fetchRetailers();
  }

  loadUserType() {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        this.userType = user.user_type?.toLowerCase() || '';
      } catch { }
    }
  }

  fetchRetailers(page: number = 1) {
    this.loading = true;
    this.errorMsg = '';

    // Determine API endpoint based on user type
    let baseUrl = '';
    if (this.userType === 'admin' || this.userType === 'super_admin') {
      baseUrl = `${environment.apiBaseUrl}/admin/retailers`;
    } else if (this.userType === 'distributor') {
      baseUrl = `${environment.apiBaseUrl}/distributor/retailers`;
    } else if (this.userType === 'staff') {
      baseUrl = `${environment.apiBaseUrl}/staff/retailers`;
    } else {
      baseUrl = `${environment.apiBaseUrl}/admin/retailers`;
    }

    let url = `${baseUrl}?page=${page}&limit=${this.pagination.limit}`;
    if (this.searchTerm && this.searchTerm.trim().length > 0) {
      url += `&search=${encodeURIComponent(this.searchTerm.trim())}`;
    }

    this.http.get<any>(url).subscribe({
      next: (res) => {
        this.loading = false;
        if (res.statusCode === 200 && res.data) {
          this.retailers = res.data.retailers;
          const pg = res.data.pagination;
          this.pagination = {
            ...pg,
            hasNext: pg.page < pg.totalPages,
            hasPrev: pg.page > 1
          };
        } else {
          this.errorMsg = res.message || 'Failed to fetch retailers';
          this.toastService.error(res.message || 'Failed to fetch retailers');
          this.retailers = [];
        }
      },
      error: (err) => {
        this.loading = false;
        this.errorMsg = err?.error?.message || 'Failed to fetch retailers';
        this.toastService.error(err?.error?.message || 'Failed to fetch retailers');
        this.retailers = [];
      }
    });
  }

  onSearch(term: string) {
    this.searchTerm = term;
    this.debounceService.debounce('retailers-search', () => this.fetchRetailers(1));
  }

  goToPage(page: number) {
    if (page < 1 || page > this.pagination.totalPages) return;
    this.fetchRetailers(page);
  }

  editRetailer(retailer: Retailer) {
    const apiUrl = `${environment.apiBaseUrl}/admin/retailer/${retailer.id}`;

    this.http.get<any>(apiUrl).subscribe({
      next: (res) => {
        if (res.statusCode === 200 && res.data) {

          // ✅ Store editing ID
          this.editingRetailerId = retailer.id;

          // ✅ Prepare data for modal
          this.selectedRetailerData = {
            // retailer_code: res.data.retailer_code || '',
            retailer_name: res.data.retailer_name || '',
            status: res.data.status || 'active',
            email: res.data.email || '',
            phone_number: res.data.phone_number || '',
            alt_phone_number: res.data.alt_phone_number || null,
            gst: res.data.gst || '',
            pan: res.data.pan || '',
            purchase_limit: Number(res.data.purchase_limit) || '',
            retailer_agency: res.data.retailer_agency || '',
            address_line1: res.data.address_line1 || '',
            address_line2: res.data.address_line2 || '',
            pincode: res.data.pincode || '',
            state: res.data.state || '',
            city: res.data.city || '',
            preferred_payment_mode: res.data.preferred_payment_mode || PaymentModeEnum.CASH,
            kyc_status: res.data.kyc_status || KycStatusEnum.PENDING,
            category: res.data.category || CategoryTypeEnum.BASIC,
            // distributor_id: res.data.distributor_id || ''
          };

          // ✅ Open modal AFTER data is ready
          this.showAddModal = true;

        } else {
          this.toastService.error(res.message || 'Failed to fetch retailer');
        }
      },
      error: (err) => {
        this.toastService.error(err?.error?.message || 'Failed to fetch retailer');
      }
    });
  }

  deleteRetailer(retailer: Retailer) {
    this.deletingRetailer = retailer;
    this.showDeleteConfirm = true;
  }

  cancelDelete() {
    this.showDeleteConfirm = false;
    this.deletingRetailer = null;
    this.deleting = false;
  }

  confirmDelete() {
    if (!this.deletingRetailer) return;
    this.deleting = true;

    this.http.delete<any>(
      `${environment.apiBaseUrl}/admin/delete-retailer/${this.deletingRetailer.id}`
    ).subscribe({
      next: (res) => {
        if (res.statusCode === 200) {
          this.toastService.success('Retailer deleted successfully!');
          this.cancelDelete();
          this.fetchRetailers(this.pagination.page);
        } else {
          this.toastService.error(res.message || 'Failed to delete retailer');
          this.deleting = false;
        }
      },
      error: (err) => {
        this.toastService.error(err.error?.message || 'Failed to delete retailer');
        this.deleting = false;
      }
    });
  }

  openAddModal() {
    if (this.userType === 'admin' || this.userType === 'super_admin' || this.userType === 'staff') {

      this.editingRetailerId = null;

      this.selectedRetailerData = {
        retailer_name: '',
        email: '',
        phone_number: '',
        alt_phone_number: null,
        gst: '',
        pan: '',
        purchase_limit: '',
        retailer_agency: '',
        address_line1: '',
        address_line2: '',
        pincode: '',
        state: '',
        city: '',
        status: 'active',
        preferred_payment_mode: PaymentModeEnum.CHEQUE,
        kyc_status: KycStatusEnum.PENDING,
        category: CategoryTypeEnum.BASIC,
      };

      this.showAddModal = true;
    }
  }

  closeAddModal() {
    this.showAddModal = false;
    this.editingRetailerId = null;
    this.selectedRetailerData = null;
  }

  handleAddRetailer(payload: any) {

    if (this.userType !== 'admin' && this.userType !== 'super_admin' && this.userType !== 'staff') {
      this.toastService.error('You do not have permission to add retailers.');
      return;
    }

    // ✅ CHECK IF EDIT MODE
    const isEdit = !!this.editingRetailerId;

    const apiUrl = isEdit
      ? `${environment.apiBaseUrl}/admin/update-retailer/${this.editingRetailerId}`
      : `${environment.apiBaseUrl}/admin/create-retailer`;

    const httpCall = isEdit
      ? this.http.patch<any>(apiUrl, payload)
      : this.http.post<any>(apiUrl, payload);

    httpCall.subscribe({
      next: (res) => {
        if (res.statusCode === 200 || res.statusCode === 201) {
          this.toastService.success(
            res.message || (isEdit ? 'Retailer updated successfully!' : 'Retailer added successfully!')
          );

          this.closeAddModal();
          this.fetchRetailers(1);
        } else {
          this.toastService.error(
            res.message || (isEdit ? 'Failed to update retailer' : 'Failed to add retailer')
          );

          if (this.addModal) {
            this.addModal.resetSubmitting();
          }
        }
      },
      error: (err) => {
        this.toastService.error(
          err?.error?.message || (isEdit ? 'Failed to update retailer' : 'Failed to add retailer')
        );

        if (this.addModal) {
          this.addModal.resetSubmitting();
        }
      }
    });
  }

  getDistributorStatusClass(status: string): string {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'status-success';
      case 'inactive':
        return 'status-failed';
      default:
        return '';
    }
  }

  // Assign Distributor Modal
  openAssignDistributorModal(retailer: Retailer) {
    this.assigningRetailerId = retailer.id;
    this.assigningRetailerName = retailer.retailer_name;
    this.showAssignModal = true;
  }

  closeAssignModal() {
    this.showAssignModal = false;
    this.assigningRetailerId = '';
    this.assigningRetailerName = '';
  }

  @ViewChild('assignModal') assignModalRef!: AssignDistributorModalComponent;

  handleAssignDistributors(event: { retailerId: string; assignments: { distributorId: string; retailerCode: string }[] }) {
    const payload = {
      assignments: event.assignments.map(a => ({
        distributor_id: a.distributorId,
        retailer_code: a.retailerCode
      }))
    };

    this.http.post<any>(
      `${environment.apiBaseUrl}/admin/retailer/${event.retailerId}/assign-distributors`,
      payload
    ).subscribe({
      next: (res) => {
        if (res.statusCode === 200) {
          this.toastService.success('Distributors assigned successfully!');
          this.closeAssignModal();
        } else {
          this.toastService.error(res.message || 'Failed to assign distributors');
          this.assignModalRef?.resetSubmitting();
        }
      },
      error: (err) => {
        this.toastService.error(err.error?.message || 'Failed to assign distributors');
        this.assignModalRef?.resetSubmitting();
      }
    });
  }

  // --- Linked Distributors Modal logic ---
  openLinkedDistributorsModal(retailer: Retailer) {
    this.selectedRetailerForLinked = retailer;
    this.showLinkedDistributorsModal = true;
  }

  closeLinkedDistributorsModal() {
    this.showLinkedDistributorsModal = false;
    this.selectedRetailerForLinked = null;
  }
}
